import {
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import moment from "moment";
import { toast } from "react-toastify";

/**
 * Envio do boleto por WhatsApp — MANUAL.
 *
 * Não existe integração de envio: o que este componente faz é abrir o WhatsApp
 * Web/app com a conversa e o texto prontos, e quem aperta "enviar" é o cliente.
 * Por isso nada aqui vai para o banco — nem o número digitado, nem registro de
 * notificação. A régua automática (e-mail e SMS) é outra coisa, configurada na
 * tela de configuração do boleto.
 *
 * O mesmo padrão da venda digitada (constants/linkPagamento.ts), só que com o
 * texto de boleto: valor, vencimento, linha digitável e o link público.
 */

const URL_BOLETO = "https://m2admin.softkuka.com.br/bolepix/view";

export interface BoletoParaWhatsApp {
  id?: string;
  nomePagador?: string | null;
  numeroPagador?: string | null;
  telefone?: string | null;
  valor?: number | string | null;
  dataVencimento?: string | null;
  dataExpiracao?: string | null;
  linhaDigitavel?: string | null;
  numeroDocumento?: string | null;
}

const somenteDigitos = (v?: string | null) => (v || "").replace(/\D/g, "");

const emReais = (valor?: number | string | null) => {
  const n = typeof valor === "string" ? Number(valor.replace(",", ".")) : valor;
  return (Number.isFinite(n as number) ? (n as number) : 0).toLocaleString(
    "pt-BR",
    { style: "currency", currency: "BRL" }
  );
};

/** Texto padrão da mensagem. Editável antes de abrir a conversa. */
export function montarMensagem(boleto: BoletoParaWhatsApp) {
  const vencimento = boleto.dataVencimento || boleto.dataExpiracao;
  const linhas = [
    `Olá${boleto.nomePagador ? `, ${boleto.nomePagador}` : ""}!`,
    "",
    `Segue seu boleto${
      boleto.numeroDocumento ? ` nº ${boleto.numeroDocumento}` : ""
    } no valor de ${emReais(boleto.valor)}${
      vencimento ? `, com vencimento em ${moment(vencimento).format("DD/MM/YYYY")}` : ""
    }.`,
  ];

  if (boleto.linhaDigitavel) {
    linhas.push("", "Linha digitável:", boleto.linhaDigitavel);
  }
  if (boleto.id) {
    linhas.push("", `Boleto completo: ${URL_BOLETO}/${boleto.id}`);
  }

  linhas.push("", "Se já efetuou o pagamento, desconsidere esta mensagem.");
  return linhas.join("\n");
}

/**
 * Abre a conversa. O 55 é fixo porque todo pagador aqui é Brasil; se o número
 * já vier com o DDI, não duplicamos.
 */
export function abrirWhatsAppBoleto(telefone: string, mensagem: string) {
  const digitos = somenteDigitos(telefone);
  const comDDI = digitos.startsWith("55") ? digitos : `55${digitos}`;

  window.open(
    `https://wa.me/${comDDI}?text=${encodeURIComponent(mensagem)}`,
    "_blank",
    "noopener,noreferrer"
  );
}

interface Props {
  boleto: BoletoParaWhatsApp | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function ModalEnviarBoletoWhatsApp({
  boleto,
  isOpen,
  onClose,
}: Props) {
  const [telefone, setTelefone] = useState("");
  const [mensagem, setMensagem] = useState("");

  // Reabrir o modal para outro boleto tem que recarregar número e texto.
  useEffect(() => {
    if (!isOpen || !boleto) return;
    setTelefone(somenteDigitos(boleto.numeroPagador || boleto.telefone));
    setMensagem(montarMensagem(boleto));
  }, [isOpen, boleto]);

  function enviar() {
    const digitos = somenteDigitos(telefone);
    // 10 = fixo com DDD, 11 = celular com DDD. Abaixo disso é número incompleto
    // e o wa.me abre uma conversa vazia sem dizer o motivo.
    if (digitos.length < 10) {
      toast.error("Informe o DDD e o número do WhatsApp.");
      return;
    }

    abrirWhatsAppBoleto(digitos, mensagem);
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "lg" }} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Enviar boleto por WhatsApp</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Vamos abrir o WhatsApp com a conversa e o texto prontos. O envio é
            feito por você, no seu WhatsApp — nada é enviado automaticamente.
          </Text>

          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Número do pagador (com DDD)
          </Text>
          <Input
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
            placeholder="51999999999"
            mb={4}
          />

          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Mensagem
          </Text>
          <Textarea
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value)}
            rows={9}
            fontSize="sm"
          />
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button colorScheme="whatsapp" leftIcon={<FaWhatsapp />} onClick={enviar}>
            Abrir WhatsApp
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/** Controla o modal sem cada tela ter que repetir os dois estados. */
export function useEnviarBoletoWhatsApp() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [boleto, setBoleto] = useState<BoletoParaWhatsApp | null>(null);

  return {
    isOpen,
    onClose,
    boleto,
    abrir: (b: BoletoParaWhatsApp) => {
      setBoleto(b);
      onOpen();
    },
  };
}
